import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoAuraVit from "../images/auravit_logo.png";
import pdfLogo from "../images/pdf logo.png";
import footerImg from "../images/footer.png";

const getStatusLabel = (status) => {
  const mapping = {
    'COMPLETED': 'Completed',
    'CANCELLED_BY_USER': 'Cancelled by User',
    'CANCELLED_BY_PRO': 'Cancelled by Professional',
    'PENDING': 'Pending Approval',
    'CONFIRMED': 'Confirmed',
    'ACCEPTED': 'Accepted',
  };
  return mapping[status] || (status ? String(status) : 'N/A');
};

export const createAppointmentSlipPDF = (appointment, overrides = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a5",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;
  const headerHeight = 42;
  const footerHeight = 28;

  const now = new Date();
  const formattedDate = now.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const formattedTime = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // ---------------- Header ----------------
  doc.setFillColor(245, 248, 255);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  // Logo (Left) — use "pdf logo.png"
  try {
    doc.addImage(pdfLogo, "PNG", margin, 8, 32, 0);
  } catch (e) {
    try {
      doc.addImage(logoAuraVit, "PNG", margin, 8, 32, 0);
    } catch (err) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(29, 78, 216);
      doc.text("AURAVIT", margin, 18);
    }
  }

  // Generated Info (Right)
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(75, 85, 99);
  doc.text(`Generated on ${formattedDate} at ${formattedTime}`, pageWidth - margin, 12, { align: "right" });
  doc.text("AuraVit-CFSI Health Education Platform", pageWidth - margin, 17, { align: "right" });

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(29, 78, 216);
  doc.text("Official Appointment Slip", pageWidth / 2, 31, { align: "center" });

  // Header divider
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(margin, headerHeight, pageWidth - margin, headerHeight);

  // ---------------- Body ----------------
  doc.setTextColor(17, 24, 39);

  const fieldsLabelW = 42;
  const valueX = margin + fieldsLabelW;
  const availableW = pageWidth - valueX - margin;
  let y = headerHeight + 14;
  const lineSpacing = 9;

  const stripHtml = (s) => String(s || "").replace(/<[^>]*>/g, "").trim();

  const serviceName = stripHtml(
    overrides.serviceName || appointment.serviceName
  );

  const proNameDisplay =
    overrides.proName !== undefined
      ? overrides.proName
      : appointment.status === "COMPLETED" || appointment.status === "ACCEPTED"
      ? (appointment.proName || "Medical Professional")
      : (appointment.proName || "To Be Assigned");

  const rows = [
    ["Reference #", overrides.reference || appointment.reference || "N/A"],
    ["Patient Name", overrides.patientName || appointment.patientName || "N/A"],
    ["Service", serviceName || "N/A"],
    ["Date", overrides.date || appointment.date || "N/A"],
    ["Time", overrides.time || appointment.time || "N/A"],
    ["Clinic", overrides.clinicName || appointment.clinicName || "N/A"],
    ["Professional", proNameDisplay],
    ["Status", getStatusLabel(overrides.status ?? appointment.status)],
  ];

  rows.forEach(([label, value]) => {
    // Label (bold, left column)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(75, 85, 99);
    doc.text(`${label}:`, margin, y);

    // Value (regular, right column, wrap if needed)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    const valueStr = String(value ?? "N/A");
    const split = doc.splitTextToSize(valueStr, availableW);
    split.forEach((line, i) => {
      doc.text(line, valueX, y + i * 4.5);
    });
    y += Math.max(lineSpacing, 4.5 * split.length);
  });

  // ---------------- 30-Minute Reminder ----------------
  const reminderLabel = "REMINDER";
  const reminderBody =
    "Please arrive at the clinic 30 minutes before your scheduled appointment time to complete check-in, screening procedures, and intake forms.";

  y += 4;
  // Light blue info box
  const boxX = margin;
  const boxW = pageWidth - 2 * margin;
  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(191, 219, 254);
  const boxH = 16;
  doc.roundedRect(boxX, y, boxW, boxH, 2, 2, "FD");

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 64, 175);
  doc.text(reminderLabel, boxX + 5, y + 6);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 64, 175);
  const bodySplit = doc.splitTextToSize(reminderBody, boxW - 10);
  bodySplit.forEach((line, i) => {
    doc.text(line, boxX + 5, y + 11 + i * 4);
  });

  // ---------------- Footer ----------------
  const footerTop = pageHeight - footerHeight;
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(margin, footerTop, pageWidth - margin, footerTop);

  doc.setFillColor(245, 248, 255);
  doc.rect(0, footerTop, pageWidth, footerHeight, "F");

  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text("AuraVit-CFSI | Empowering Health Education and Wellness", pageWidth / 2, pageHeight - 16, { align: "center" });
  doc.text("This is an official record of the appointment.", pageWidth / 2, pageHeight - 10, { align: "center" });

  doc.save(`Appointment_${overrides.reference || appointment.reference || (appointment.id || "")}.pdf`);
};

export const createPDF = (options = {}) => {
  const {
    orientation = "landscape",
    unit = "mm",
    format = "a4",
    medicalProfessionalName = null,
  } = options;

  const doc = new jsPDF({
    orientation,
    unit,
    format,
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const headerHeight = 40;
  const footerHeight = 30;
  const contentStartY = headerHeight + 10;
  const contentEndY = pageHeight - footerHeight - 10;

  // Header Data
  const now = new Date();
  const formattedDate = now.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const formattedTime = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Page Event Handler for Header & Footer
  const addHeaderFooter = () => {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Header
      doc.setFillColor(245, 248, 255);
      doc.rect(0, 0, pageWidth, headerHeight, "F");
      
      // Logo (Left) - maintain aspect ratio
      try {
        // Use width 50mm, height 0 to auto-calculate height to maintain aspect ratio
        doc.addImage(logoAuraVit, "PNG", margin, 8, 50, 0);
      } catch (e) {
        console.log("Logo loading error");
      }
      
      // Title (Center)
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(29, 78, 216);
      const reportTitle = options.title || "Report";
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.text(reportTitle, pageWidth / 2 - titleWidth / 2, 22);
      
      // Generated Info (Right)
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(75, 85, 99);
      doc.text(`Generated on ${formattedDate} at ${formattedTime}`, pageWidth - margin, 15, { align: "right" });
      doc.text("AuraVit-CFSI Health Education Platform", pageWidth - margin, 22, { align: "right" });
      if (medicalProfessionalName) {
        doc.text(`Medical Professional: ${medicalProfessionalName}`, pageWidth - margin, 29, { align: "right" });
      }
      
      // Header Divider
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, headerHeight, pageWidth - margin, headerHeight);

      // Footer
      doc.setFillColor(245, 248, 255);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");
      
      // Footer Content
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text("AuraVit-CFSI | Empowering Health Education and Wellness", margin, pageHeight - 12);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 12, { align: "right" });
      
      // Footer Divider
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, pageHeight - footerHeight, pageWidth - margin, pageHeight - footerHeight);
    }
  };

  return {
    doc,
    pageWidth,
    pageHeight,
    margin,
    headerHeight,
    footerHeight,
    contentStartY,
    contentEndY,
    formattedDate,
    formattedTime,
    addHeaderFooter,
    autoTable: (tableOptions) => {
      autoTable(doc, {
        theme: "grid",
        headStyles: {
          fillColor: [29, 78, 216],
          textColor: 255,
          fontSize: 11,
          fontStyle: "bold",
          halign: "left",
        },
        bodyStyles: {
          fontSize: 10,
          textColor: 55,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        styles: {
          cellPadding: 6,
          overflow: "linebreak",
        },
        margin: { top: contentStartY, bottom: footerHeight + 10, left: margin, right: margin },
        ...tableOptions,
      });
      addHeaderFooter();
    },
    save: (filename) => {
      addHeaderFooter();
      doc.save(filename);
    },
  };
};
